import type { SVGProps } from 'react'
import { cn } from '../lib/cn'

/* ------------------------------------------------------------------
   آیکون‌های خطی — مجموعهٔ کوچکِ مشترک
   ------------------------------------------------------------------
   🔴 چرا مجموعهٔ خودی و نه یک کتابخانه:

   هیچ کتابخانهٔ آیکونی در پروژه نصب نیست و طبق بند ۲۰ حق خواندن از
   CDN هم نداریم. افزودنِ یک وابستگیِ تازه برای چند آیکون، هم حجم
   خروجی را بالا می‌برد و هم یک سامانهٔ بصریِ دوم وارد می‌کند که با
   آیکون‌های خطیِ موجود (تقویم، فلش، تیک) هم‌خانواده نیست.

   ⚠️ قرارداد شکلی از همان آیکون‌های موجود گرفته شده تا تازه‌ها وصله
   نباشند: کادر ۲۴، بدون `fill`، ضخامت ۱٫۷، سرِ خطوط گرد.

   ⚠️ همه `aria-hidden` هستند. آیکونِ داخل دکمه هرگز تنها حاملِ معنا
   نیست — متنِ دکمه آن را می‌گوید — و خوانده‌شدنش فقط تکرارِ همان متن
   برای کاربر صفحه‌خوان است. برای دکمهٔ فقط‌آیکون، `IconButton` خودش
   `aria-label` اجباری دارد.
   ------------------------------------------------------------------ */

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'>

/**
 * پوستهٔ مشترک همهٔ آیکون‌ها.
 *
 * ⚠️ `size-4` پیش‌فرض است چون اندازهٔ متنِ دکمه در اندازهٔ `md` همان
 * است؛ با `className` قابل بازنویسی است.
 *
 * ⚠️ `shrink-0` حیاتی است: آیکون داخل دکمه‌ای که جا کم می‌آورد بدون
 * آن فشرده و بیضی می‌شود.
 */
function Svg({ className, children, ...rest }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      stroke="currentColor"
      className={cn('size-4 shrink-0', className)}
      {...rest}
    >
      {children}
    </svg>
  )
}

/** تیک — تأیید */
export function CheckIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="m4.5 12.5 5 5 10-11" />
    </Svg>
  )
}

/** ضربدر — رد کردن */
export function CloseIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6 6l12 12M18 6L6 18" />
    </Svg>
  )
}

/**
 * فلشِ بازگشتی — برگشت برای اصلاح.
 *
 * ⚠️ آینهٔ نسخهٔ لاتین است: قلاب به **راست** برمی‌گردد.
 *
 * 🔴 در چیدمان راست‌به‌چپ «عقب» یعنی سمت راست. نسخهٔ اول قلابش به چپ
 * بود (همان شکلِ متعارفِ undo در رابط‌های لاتین) و در آزمون مرورگر
 * کنارِ متنِ فارسی خلافِ جهت خوانده می‌شد — یعنی «جلو» نه «برگشت».
 */
export function UndoIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M15 14l5-5-5-5" />
      <path d="M20 9H10a6 6 0 0 0 0 12h3" />
    </Svg>
  )
}

/** سطلِ زباله — حذف و انصراف */
export function TrashIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 7h16M10 4h4M6 7l1 13h10l1-13" />
      <path d="M10 11v6M14 11v6" />
    </Svg>
  )
}

/** بعلاوه — ساختنِ چیز تازه */
export function PlusIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  )
}

/** فلشِ رو به عقب — انصراف و بازگشت.
    ⚠️ در چیدمان راست‌به‌چپ «عقب» یعنی سمت راست، پس نوکِ فلش راست است. */
export function BackIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M9 6l6 6-6 6" />
    </Svg>
  )
}

/**
 * سند با فلشِ رو به پایین — گرفتنِ فایل از سامانه.
 *
 * ⚠️ فلشِ تنها کافی نبود: «دانلود» در این سامانه همیشه یک **سند** است
 * (نامه، گزارش، اصلِ فایل) و فلشِ خالی از کنشِ «بارگذاری» قابل تشخیص
 * نیست — هر دو یک فلش‌اند با جهتِ مخالف، و در نگاهِ سریع اشتباه
 * گرفته می‌شوند.
 *
 * ⚠️ گوشهٔ تاشدهٔ کاغذ عمداً هست: بدونش شکل یک مستطیلِ ساده می‌شود و
 * در اندازهٔ ۱۶ پیکسل دیگر «سند» خوانده نمی‌شود.
 */
export function DocumentDownloadIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5" />
      <path d="M12 11.5v5" />
      <path d="m9.5 14 2.5 2.5 2.5-2.5" />
    </Svg>
  )
}

/** کاغذ و قلم — ثبت و ارسال فرم */
export function SendIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M20 4 3 10.5l6.5 3L13 20l7-16Z" />
      <path d="M9.5 13.5 20 4" />
    </Svg>
  )
}
